/**
 * Bus efficiency analyzer: MPG, cost, rating, insights, and report from fuel data.
 * Uses tank capacity and refuel data (from post-trip surveys and "I refueled" events).
 */

const L100KM_TO_MPG = 235.214583; // 1 L/100km = 235.21... MPG (US gallon)
const DEFAULT_FILL_FRACTION = 0.8;

/** Fallback tank size (litres) when fleet admin has not set `fuelTankCapacityL` on the bus. */
export const DEFAULT_TANK_CAPACITY_L = 150;

/**
 * Fallback benchmark (L/100 km) used only in `getEfficiencyRating` thresholds.
 * Not used for scoring — scoring requires a per-bus `benchmarkKmPerL` set by the admin.
 */
export const DEFAULT_BENCHMARK_L100KM = 10.5;

export interface BusFuelInput {
  totalKm30: number;
  refuelCount30: number;
  needsRefuelCount: number;
  tankCapacityL: number;
  fillFraction?: number;
  costPerLiter?: number;
  /** Actual litres added as reported by drivers. When present and > 0, used instead of the tank-capacity estimate. */
  totalLitresAdded?: number;
  /** Σ km between consecutive odometer readings (clean, unflagged fills only)
   *  inside the analytics window. With `odoWindowLitres`, this is the
   *  PREFERRED measurement: the odometer counts ALL driving, GPS only the
   *  tracked trips (Gabriel, 2026-08-17 — "the odometer is the referee"). */
  odoWindowKm?: number;
  /** Σ litres of those same odometer-bracketed fills. */
  odoWindowLitres?: number;
  /**
   * User-set efficiency benchmark in km per litre for this specific bus (required for scoring).
   * Score = (actual km/L ÷ age-adjusted benchmark km/L) × 100, capped at 100%.
   * Without this, `efficiencyScorePercent` returns null.
   */
  benchmarkKmPerL?: number;
  /**
   * Bus age in years (today − year of manufacture). Older buses are held to a gentler
   * benchmark: a new-bus target is unfair to an aging engine. See `ageAdjustedBenchmarkKmPerL`.
   * Optional/undefined → no age adjustment (treated as a brand-new bus).
   */
  busAgeYears?: number;
}

/**
 * Engines lose efficiency with age. We relax the km/L target by this fraction per year,
 * floored at `MAX_AGE_DEGRADATION` so a very old bus is never scored against zero.
 * Example: a 5-year-old bus with a 0.02/yr rate is held to 90% of its set benchmark.
 */
export const AGE_DEGRADATION_PER_YEAR = 0.02;
export const MAX_AGE_DEGRADATION = 0.30;

/**
 * The km/L target a bus is actually scored against, after relaxing the admin-set benchmark
 * for the bus's age. Returns the raw benchmark when no age (or age ≤ 0) is provided.
 */
export function ageAdjustedBenchmarkKmPerL(benchmarkKmPerL: number, busAgeYears?: number): number {
  if (busAgeYears == null || busAgeYears <= 0) return benchmarkKmPerL;
  const degradation = Math.min(MAX_AGE_DEGRADATION, busAgeYears * AGE_DEGRADATION_PER_YEAR);
  return benchmarkKmPerL * (1 - degradation);
}

export interface EfficiencyResult {
  /** Liters per 100 km (estimated) */
  l100km: number | null;
  /** Miles per gallon (US) */
  mpg: number | null;
  /** Liters used in last 30 days (estimated) */
  litersUsed30: number;
  /** Estimated monthly cost (if costPerLiter provided) */
  monthlyCost: number | null;
  /** Excellent | Good | Fair | Poor */
  rating: "Excellent" | "Good" | "Fair" | "Poor";
  /** Human-readable refueling insight */
  refuelingInsight: string;
  /** Full report text */
  report: string;
}

function getEfficiencyRating(l100km: number): EfficiencyResult["rating"] {
  if (l100km < 8) return "Excellent";
  if (l100km < 10) return "Good";
  if (l100km < 12) return "Fair";
  return "Poor";
}

/**
 * Calculate L/100km from actual litres added (preferred) or estimated from tank capacity × refuel count.
 * Formula: Total KM traveled ÷ Total litres added = km/L → invert × 100 for L/100km.
 */
export function calculateL100km(input: BusFuelInput): number | null {
  const { totalKm30, refuelCount30, tankCapacityL, fillFraction = DEFAULT_FILL_FRACTION, totalLitresAdded } = input;
  if (totalKm30 <= 0) return null;
  if (totalLitresAdded != null && totalLitresAdded > 0) {
    return (totalLitresAdded / totalKm30) * 100;
  }
  if (refuelCount30 < 1) return null;
  const litersUsed = refuelCount30 * tankCapacityL * fillFraction;
  return (litersUsed / totalKm30) * 100;
}

export function calculateMPG(input: BusFuelInput): number | null {
  const l100 = calculateL100km(input);
  return l100 != null ? L100KM_TO_MPG / l100 : null;
}

/**
 * Estimated monthly fuel cost (extends 30-day usage to monthly).
 */
export function calculateMonthlyCost(input: BusFuelInput): number | null {
  const { costPerLiter, totalKm30, refuelCount30, tankCapacityL, fillFraction = DEFAULT_FILL_FRACTION, totalLitresAdded } = input;
  if (costPerLiter == null || costPerLiter <= 0) return null;
  if (totalKm30 <= 0) return null;
  const litersUsed30 = (totalLitresAdded != null && totalLitresAdded > 0)
    ? totalLitresAdded
    : refuelCount30 >= 1 ? refuelCount30 * tankCapacityL * fillFraction : null;
  if (litersUsed30 == null) return null;
  return Math.round(litersUsed30 * costPerLiter * 100) / 100;
}

export function getEfficiencyRatingFromInput(input: BusFuelInput): EfficiencyResult["rating"] {
  const l100 = calculateL100km(input);
  if (l100 == null) return "Poor";
  return getEfficiencyRating(l100);
}

/**
 * Single 0–100 score: higher is better.
 * Requires `input.benchmarkKmPerL` — the per-bus km/L efficiency target set by the fleet admin.
 * The target is relaxed for the bus's age (`input.busAgeYears`) so older buses are scored fairly.
 * Score = (actual km/L ÷ age-adjusted benchmark km/L) × 100, capped at 100%.
 * Returns null when no benchmark is set or consumption cannot be estimated.
 */
export function efficiencyScorePercent(
  input: BusFuelInput
): number | null {
  if (input.benchmarkKmPerL == null || input.benchmarkKmPerL <= 0) return null;
  const l100 = calculateL100km(input);
  if (l100 == null || l100 <= 0) return null;
  const actualKmPerL = 100 / l100;
  const target = ageAdjustedBenchmarkKmPerL(input.benchmarkKmPerL, input.busAgeYears);
  return Math.min(100, Math.round((actualKmPerL / target) * 100));
}

/**
 * Human-readable insight about refueling patterns.
 */
export function getRefuelingInsight(input: BusFuelInput): string {
  const { totalKm30, refuelCount30, needsRefuelCount } = input;
  if (totalKm30 <= 0) return "No trips in the last 30 days. Start routes to see fuel insights.";
  if (refuelCount30 === 0) return 'No refuel events recorded. Drivers can tap "I refueled" in Settings to improve estimates.';
  const tripsNeedingRefuel = needsRefuelCount;
  if (tripsNeedingRefuel > 0) return `${tripsNeedingRefuel} trip(s) reported "needs refuel soon" in post-trip surveys. Consider refueling more often.`;
  return `Refueling pattern looks consistent (${refuelCount30} refuel event(s) in 30 days).`;
}

/**
 * Compare this bus with another (MPG difference).
 */
export function compareWith(
  thisInput: BusFuelInput,
  otherInput: BusFuelInput
): { thisMpg: number | null; otherMpg: number | null; differenceMpg: number | null; better: "this" | "other" | "tie" } {
  const thisMpg = calculateMPG(thisInput);
  const otherMpg = calculateMPG(otherInput);
  if (thisMpg == null || otherMpg == null) {
    return { thisMpg, otherMpg, differenceMpg: null, better: "tie" };
  }
  const diff = thisMpg - otherMpg;
  const better = diff > 0 ? "this" : diff < 0 ? "other" : "tie";
  return { thisMpg, otherMpg, differenceMpg: diff, better };
}

/**
 * Generate full efficiency report.
 */
export function generateReport(input: BusFuelInput, busName: string): EfficiencyResult {
  const l100km = calculateL100km(input);
  const mpg = calculateMPG(input);
  const fillFraction = input.fillFraction ?? DEFAULT_FILL_FRACTION;
  const litersUsed30 =
    (input.totalLitresAdded != null && input.totalLitresAdded > 0)
      ? input.totalLitresAdded
      : input.refuelCount30 >= 1 && input.totalKm30 > 0
        ? input.refuelCount30 * input.tankCapacityL * fillFraction
        : 0;
  const monthlyCost = calculateMonthlyCost(input);
  const rating = l100km != null ? getEfficiencyRating(l100km) : "Poor";
  const refuelingInsight = getRefuelingInsight(input);

  const reportLines: string[] = [
    `Fuel efficiency report: ${busName}`,
    `Period: Last 30 days`,
    ``,
    `Distance: ${input.totalKm30.toFixed(0)} km`,
    `Refuel events: ${input.refuelCount30}`,
    `Trips reporting "needs refuel soon": ${input.needsRefuelCount}`,
    ``,
    l100km != null
      ? `Consumption: ${l100km.toFixed(1)} L/100 km (${mpg != null ? `${mpg.toFixed(1)} MPG` : "—"})`
      : `Consumption: Not enough data (need at least 1 refuel event and distance).`,
    `Rating: ${rating}`,
    monthlyCost != null ? `Est. monthly cost: ${monthlyCost.toFixed(2)} (at given cost per liter)` : ``,
    ``,
    `Insight: ${refuelingInsight}`,
  ];

  return {
    l100km: l100km ?? null,
    mpg,
    litersUsed30,
    monthlyCost,
    rating,
    refuelingInsight,
    report: reportLines.filter(Boolean).join("\n"),
  };
}
